// FE-SCRM-049 — REST client for the Prospect surface.
//
// Wraps the BE-SCRM-003 endpoints under /api/v1/sales/prospects plus
// the broker-firm autocomplete and the FE-SCRM-060 picker endpoints
// (tag suggestions, global search).

import { api } from "@/api/client";

import type {
  Form5500FilterStatus,
  Prospect, ProspectCreateInput, ProspectListResponse,
} from "./types";


export interface FetchProspectsQuery {
  include_archived?: boolean;
  /** FE-SCRM-063 — Form 5500 link-state filter facet. The BE applies
   *  the SQL predicate so the panel doesn't have to ship the
   *  ``form5500_match_attempted_at`` column down to client filtering. */
  form5500_status?: Form5500FilterStatus | null;
}


export function fetchProspects(
  query: FetchProspectsQuery,
  opts?: { signal?: AbortSignal },
): Promise<ProspectListResponse> {
  const params = new URLSearchParams();
  if (query.include_archived) params.set("include_archived", "true");
  if (query.form5500_status) {
    params.set("form5500_status", query.form5500_status);
  }
  const qs = params.toString();
  return api.get<ProspectListResponse>(
    `/api/v1/sales/prospects${qs ? `?${qs}` : ""}`,
    opts,
  );
}


export function createProspect(
  input: ProspectCreateInput,
): Promise<Prospect> {
  return api.post<Prospect>("/api/v1/sales/prospects", input);
}


export function archiveProspect(prospect_id: string): Promise<Prospect> {
  return api.post<Prospect>(
    `/api/v1/sales/prospects/${prospect_id}/archive`, {},
  );
}


export function restoreProspect(prospect_id: string): Promise<Prospect> {
  return api.post<Prospect>(
    `/api/v1/sales/prospects/${prospect_id}/restore`, {},
  );
}


export interface BrokerFirmSuggestion { name: string }


export function fetchBrokerFirmSuggestions(
  q: string,
  opts?: { signal?: AbortSignal },
): Promise<{ rows: BrokerFirmSuggestion[] }> {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  return api.get(
    `/api/v1/sales/broker-firms/suggestions${
      params.toString() ? `?${params.toString()}` : ""
    }`,
    opts,
  );
}


export interface TagSuggestionRow { tag: string; count: number }


export function fetchTagSuggestions(
  q: string,
  entity_type: "prospect" | "opportunity" | "contact" = "prospect",
  opts?: { signal?: AbortSignal },
): Promise<{ tags: TagSuggestionRow[] }> {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  params.set("entity_type", entity_type);
  return api.get(
    `/api/v1/sales/tags/suggestions?${params.toString()}`, opts,
  );
}


export interface GlobalSearchHit {
  id: string;
  label: string;
  sublabel?: string;
  route?: string;
}


export interface GlobalSearchResponse {
  prospects: GlobalSearchHit[];
  opportunities: GlobalSearchHit[];
  sales_contacts: GlobalSearchHit[];
  carriers: GlobalSearchHit[];
  carrier_contacts: GlobalSearchHit[];
}


export function fetchGlobalSearch(
  q: string,
  opts?: { signal?: AbortSignal },
): Promise<GlobalSearchResponse> {
  const params = new URLSearchParams({ q });
  return api.get<GlobalSearchResponse>(
    `/api/v1/sales/search?${params.toString()}`, opts,
  );
}
